import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FaEdit, FaTrash } from "react-icons/fa";
import { Header } from "../../components/Header";
import { isAdmin } from "../../constants/permisos";
import { useAuthContext } from "../../context/auth";
import {
  deleteIntermediario,
  getClientes,
  getIntermediarios,
  getUsers,
  log,
  quitarCaracteresEspeciales,
  saveIntermediario,
  updateIntermediario,
} from "../../utils";
import styles from "./stylesIntermediarios.module.scss";

const EMPTY_FORM = { nombre: "", comision: "", comision_2: "", comision_2_fechainicio: "", comision_2_fechafin: "" };

export const CargarIntermediario = () => {
  const [intermediarios, setIntermediarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [searchNombre, setSearchNombre] = useState("");
  const [sortColumn, setSortColumn] = useState("nombre");
  const [sortDirection, setSortDirection] = useState("asc");
  const [saving, setSaving] = useState(false);
  const [detailModal, setDetailModal] = useState(null); // { type: "clientes"|"usuarios", inter }

  const { pais, id: userId } = useAuthContext();
  const esAdmin = isAdmin(userId);

  // --- Data loading ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newIntermediarios, newClientes, newUsuarios] = await Promise.all([
          getIntermediarios({ pais }),
          getClientes({ pais }).catch(() => []),
          getUsers().catch(() => []),
        ]);
        setIntermediarios(newIntermediarios || []);
        setClientes(newClientes || []);
        setUsuarios(newUsuarios || []);
      } catch (error) {
        log.error("CargarIntermediario fetchData", error);
        toast.error("Error al cargar datos");
      }
    };
    fetchData();
  }, [pais]);

  // --- Conteos por intermediario ---
  const clientesPorInter = useMemo(() => {
    const map = {};
    for (const c of clientes) {
      const agId = parseInt(c.agencia, 10);
      if (!isNaN(agId)) map[agId] = (map[agId] || 0) + 1;
    }
    return map;
  }, [clientes]);

  const usuariosPorInter = useMemo(() => {
    const map = {};
    for (const u of usuarios) {
      if (u.agencia) {
        const agId = parseInt(u.agencia, 10);
        if (!isNaN(agId)) map[agId] = (map[agId] || 0) + 1;
      }
    }
    return map;
  }, [usuarios]);

  // --- Comisión vigente ---
  const getComisionVigente = useCallback((inter) => {
    if (inter.comision_2 == null || !inter.comision_2_fechainicio || !inter.comision_2_fechafin) {
      return inter.comision || 0;
    }
    const hoy = new Date();
    const inicio = new Date(inter.comision_2_fechainicio);
    const fin = new Date(inter.comision_2_fechafin);
    if (hoy >= inicio && hoy <= fin) return inter.comision_2;
    return inter.comision || 0;
  }, []);

  // --- Sort & filter ---
  const handleSort = useCallback(
    (column) => {
      setSortDirection(sortColumn === column && sortDirection === "asc" ? "desc" : "asc");
      setSortColumn(column);
    },
    [sortColumn, sortDirection]
  );

  const sortedIntermediarios = useMemo(() => {
    const searchLower = searchNombre.toLowerCase().trim();
    return intermediarios
      .filter((i) => !searchLower || i.nombre?.toLowerCase().includes(searchLower))
      .sort((a, b) => {
        let valA = a[sortColumn] ?? "";
        let valB = b[sortColumn] ?? "";
        if (sortColumn === "vigente") {
          valA = getComisionVigente(a);
          valB = getComisionVigente(b);
        } else if (sortColumn === "clientes") {
          valA = clientesPorInter[a.id] || 0;
          valB = clientesPorInter[b.id] || 0;
        } else if (sortColumn === "usuarios") {
          valA = usuariosPorInter[a.id] || 0;
          valB = usuariosPorInter[b.id] || 0;
        }
        if (typeof valA === "number" && typeof valB === "number") {
          return sortDirection === "asc" ? valA - valB : valB - valA;
        }
        return sortDirection === "asc"
          ? String(valA).localeCompare(String(valB), "es")
          : String(valB).localeCompare(String(valA), "es");
      });
  }, [intermediarios, searchNombre, sortColumn, sortDirection, clientesPorInter, usuariosPorInter, getComisionVigente]);

  // --- Modal handlers ---
  const handleOpenAdd = useCallback(() => {
    setEditingItem(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  }, []);

  const handleOpenEdit = useCallback((inter) => {
    setEditingItem(inter);
    setFormData({
      nombre: inter.nombre || "",
      comision: inter.comision ?? "",
      comision_2: inter.comision_2 ?? "",
      comision_2_fechainicio: inter.comision_2_fechainicio || "",
      comision_2_fechafin: inter.comision_2_fechafin || "",
    });
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingItem(null);
    setFormData(EMPTY_FORM);
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // --- Save (create or update) ---
  const handleSave = useCallback(async () => {
    if (!formData.nombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (formData.comision === "" || formData.comision === null) {
      toast.error("La comisión es obligatoria");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nombre: quitarCaracteresEspeciales(formData.nombre.trim()),
        comision: parseFloat(formData.comision),
        comision_2: formData.comision_2 !== "" ? parseFloat(formData.comision_2) : null,
        comision_2_fechainicio: formData.comision_2 !== "" ? formData.comision_2_fechainicio || null : null,
        comision_2_fechafin: formData.comision_2 !== "" ? formData.comision_2_fechafin || null : null,
        lastupdate: new Date().toISOString(),
      };

      if (editingItem) {
        // Update
        await updateIntermediario({ id: editingItem.id, ...payload });
        setIntermediarios((prev) => prev.map((i) => (i.id === editingItem.id ? { ...i, ...payload, id: i.id } : i)));
        toast.success("Agencia actualizada correctamente");
      } else {
        // Create
        payload.pais = pais;
        const result = await saveIntermediario(payload);
        const newItem = Array.isArray(result) ? result[0] : result;
        setIntermediarios((prev) => [...prev, newItem]);
        toast.success("Agencia creada correctamente");
      }
      handleCloseModal();
    } catch (error) {
      if (error.code === "23505") {
        toast.error("Ya existe una agencia con ese nombre");
      } else {
        log.error("handleSave intermediario", error);
        toast.error("Error al guardar la agencia");
      }
    } finally {
      setSaving(false);
    }
  }, [formData, editingItem, pais, handleCloseModal]);

  // --- Delete ---
  const handleDelete = useCallback(
    async (inter) => {
      const numClientes = clientesPorInter[inter.id] || 0;
      const numUsuarios = usuariosPorInter[inter.id] || 0;

      let msg = `¿Eliminar "${inter.nombre}"?`;
      if (numClientes > 0 || numUsuarios > 0) {
        msg += `\n\nADVERTENCIA: Tiene ${numClientes} cliente(s) y ${numUsuarios} usuario(s) asociados.`;
      }

      if (!window.confirm(msg)) return;

      try {
        await deleteIntermediario(inter.id);
        setIntermediarios((prev) => prev.filter((i) => i.id !== inter.id));
        toast.success("Agencia eliminada");
      } catch (error) {
        log.error("handleDelete intermediario", error);
        toast.error("Error al eliminar la agencia");
      }
    },
    [clientesPorInter, usuariosPorInter]
  );

  // --- Detail modal (clientes/usuarios asociados) ---
  const handleOpenDetail = useCallback((type, inter) => {
    setDetailModal({ type, inter });
  }, []);

  const detailItems = useMemo(() => {
    if (!detailModal) return [];
    const { type, inter } = detailModal;
    if (type === "clientes") {
      return clientes.filter((c) => parseInt(c.agencia, 10) === inter.id);
    }
    return usuarios.filter((u) => parseInt(u.agencia, 10) === inter.id);
  }, [detailModal, clientes, usuarios]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  // --- Render helpers ---
  const sortIndicator = (col) => {
    if (sortColumn !== col) return "";
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  const filtersContent = useMemo(
    () => (
      <div className={styles.filtersContainer}>
        <input
          type="text"
          placeholder="Buscar nombre..."
          value={searchNombre}
          onChange={(e) => setSearchNombre(e.target.value)}
        />
        {esAdmin && (
          <button type="button" className="btn btn-primary" onClick={handleOpenAdd}>
            + Agregar Agencia
          </button>
        )}
      </div>
    ),
    [searchNombre, esAdmin, handleOpenAdd]
  );

  return (
    <div className={styles.containerIntermediarios}>
      <Header expandChildren>{filtersContent}</Header>

      <div className={styles.intermediariosTable}>
        <table>
          <thead>
            <tr>
              <th className={styles.colNombre} onClick={() => handleSort("nombre")}>
                Nombre{sortIndicator("nombre")}
              </th>
              <th className={styles.colComision} onClick={() => handleSort("comision")}>
                Comisión{sortIndicator("comision")}
              </th>
              <th className={styles.colComision2} onClick={() => handleSort("comision_2")}>
                Comisión 2{sortIndicator("comision_2")}
              </th>
              <th className={styles.colVigente} onClick={() => handleSort("vigente")}>
                Vigente{sortIndicator("vigente")}
              </th>
              <th className={styles.colClientes} onClick={() => handleSort("clientes")}>
                Clientes{sortIndicator("clientes")}
              </th>
              <th className={styles.colUsuarios} onClick={() => handleSort("usuarios")}>
                Usuarios{sortIndicator("usuarios")}
              </th>
              {esAdmin && <th className={styles.colAcciones}>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {sortedIntermediarios.map((inter) => {
              const numClientes = clientesPorInter[inter.id] || 0;
              const numUsuarios = usuariosPorInter[inter.id] || 0;
              return (
                <tr key={inter.id}>
                  <td className={styles.colNombre}>{inter.nombre}</td>
                  <td className={styles.colComision}>{inter.comision}%</td>
                  <td className={styles.colComision2}>{inter.comision_2 != null ? `${inter.comision_2}%` : "-"}</td>
                  <td className={styles.colVigente}>
                    <strong>{getComisionVigente(inter)}%</strong>
                  </td>
                  <td className={styles.colClientes}>
                    <span
                      className={`${styles.badge} ${numClientes > 0 ? styles.active : ""} ${numClientes > 0 ? styles.clickable : ""}`}
                      onClick={() => numClientes > 0 && handleOpenDetail("clientes", inter)}
                    >
                      {numClientes}
                    </span>
                  </td>
                  <td className={styles.colUsuarios}>
                    <span
                      className={`${styles.badge} ${numUsuarios > 0 ? styles.active : ""} ${numUsuarios > 0 ? styles.clickable : ""}`}
                      onClick={() => numUsuarios > 0 && handleOpenDetail("usuarios", inter)}
                    >
                      {numUsuarios}
                    </span>
                  </td>
                  {esAdmin && (
                    <td className={styles.colAcciones}>
                      <button className={styles.actionBtn} onClick={() => handleOpenEdit(inter)} title="Editar">
                        <FaEdit />
                      </button>
                      <button
                        className={`${styles.actionBtn} ${styles.deleteBtn}`}
                        onClick={() => handleDelete(inter)}
                        title="Eliminar"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {sortedIntermediarios.length === 0 && (
              <tr>
                <td colSpan={esAdmin ? 7 : 6} style={{ textAlign: "center", padding: "20px" }}>
                  No se encontraron agencias
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editingItem ? "Editar Agencia" : "Agregar Agencia"}</h3>
              <button onClick={handleCloseModal}>&times;</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Nombre</label>
                <input
                  className="form-control"
                  name="nombre"
                  type="text"
                  placeholder="Nombre de la agencia"
                  value={formData.nombre}
                  onChange={handleInputChange}
                />
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Comisión %</label>
                  <input
                    className="form-control"
                    name="comision"
                    type="number"
                    step="0.1"
                    placeholder="10"
                    value={formData.comision}
                    onChange={handleInputChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Comisión 2 % (opcional)</label>
                  <input
                    className="form-control"
                    name="comision_2"
                    type="number"
                    step="0.1"
                    placeholder="-"
                    value={formData.comision_2}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              {formData.comision_2 !== "" && formData.comision_2 !== null && (
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Fecha inicio comisión 2</label>
                    <input
                      className="form-control"
                      name="comision_2_fechainicio"
                      type="date"
                      value={formData.comision_2_fechainicio}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Fecha fin comisión 2</label>
                    <input
                      className="form-control"
                      name="comision_2_fechafin"
                      type="date"
                      value={formData.comision_2_fechafin}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailModal && (
        <div className={styles.modalOverlay} onClick={() => setDetailModal(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                {detailModal.type === "clientes" ? "Clientes" : "Usuarios"} — {detailModal.inter.nombre}
              </h3>
              <button onClick={() => setDetailModal(null)}>&times;</button>
            </div>
            <div className={styles.modalBody}>
              {detailItems.length === 0 ? (
                <p style={{ textAlign: "center", color: "#6c757d" }}>Sin registros</p>
              ) : detailModal.type === "clientes" ? (
                <table className={styles.detailTable}>
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Empresa</th>
                      <th>Último contacto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailItems.map((c) => (
                      <tr key={c.id}>
                        <td>{c.nombre}</td>
                        <td>{c.empresa !== c.producto ? c.empresa : "-"}</td>
                        <td>{formatDate(c.ultimo_contacto_fecha)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className={styles.detailTable}>
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Email</th>
                      <th>Cargo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailItems.map((u) => (
                      <tr key={u.id}>
                        <td>{`${u.nombre} ${u.apellido || ""}`.trim()}</td>
                        <td>{u.email || "-"}</td>
                        <td>{u.cargo || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className="btn btn-secondary" onClick={() => setDetailModal(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
